import { removeEmptyKeys } from 'common/utils';
import type { CreateUserData } from 'modules/account/services/useCreateAccount';
import type { AccountInfo, RolesToAssign, Unit, UserInfo } from 'modules/account/types';

export interface CreatePayloadValidationError {
  field: string;
  message: string;
}

/**
 * Validate create payload before API call.
 * Returns empty array if valid, otherwise list of errors.
 */
export function validateCreatePayload(params: {
  accountInfo: AccountInfo;
  userInfo: UserInfo;
  unitAddress: Unit | undefined;
}): CreatePayloadValidationError[] {
  const { accountInfo, userInfo, unitAddress } = params;
  const errors: CreatePayloadValidationError[] = [];

  if (!accountInfo.username?.trim() && !accountInfo.email?.trim()) {
    errors.push({ field: 'username', message: 'Username or email required' });
  }

  if (!accountInfo.password?.trim()) {
    errors.push({ field: 'password', message: 'Password required' });
  }

  if (!userInfo.name?.trim()) {
    errors.push({ field: 'name', message: 'Name required' });
  }

  if (!userInfo.organizationTypeId) {
    errors.push({
      field: 'organizationTypeId',
      message: 'Organization type required',
    });
  }

  if (!userInfo.unitId) {
    errors.push({ field: 'unitId', message: 'Unit required' });
  }

  if (!userInfo.jobTitleId) {
    errors.push({ field: 'jobTitleId', message: 'Job title required' });
  }

  if (!userInfo.phone?.trim()) {
    errors.push({ field: 'phone', message: 'Phone required' });
  }

  if (!unitAddress) {
    errors.push({ field: 'unitAddress', message: 'Unit address required' });
  }

  return errors;
}

interface BuildPayloadParams {
  accountInfo: AccountInfo;
  userInfo: UserInfo;
  unitAddress: Unit;
  rolesToAssign: RolesToAssign;
  isCQQL: boolean;
}

/**
 * Format roleAssignment theo cấu trúc yêu cầu của API POST /management/user
 */
function formatRolesAssignments(
  rolesToAssign: RolesToAssign,
  isCQQL: boolean
): CreateUserData['roleAssignment'] {
  const {
    forClientBusiness,
    forGovAgencyBusiness,
    forAdminRoles: forAdmin,
    forSystemRoles: forSystem,
  } = rolesToAssign.rolesAssignment;

  return {
    forClientBusiness: isCQQL ? [] : forClientBusiness,
    forGovAgencyBusiness: isCQQL ? forGovAgencyBusiness : [],
    forAdmin,
    forSystem,
  };
}

/**
 * Tạo payload cho API tạo người dùng mới
 */
export function buildCreateUserPayload(params: BuildPayloadParams): CreateUserData {
  const { accountInfo, userInfo, unitAddress, rolesToAssign, isCQQL } = params;

  const orgId = userInfo.organizationTypeId;
  const isHvs = orgId === 'HVS';
  const orgType = isHvs ? 'system' : orgId === 'CQQL' ? 'govAgency' : 'client';

  return {
    username: accountInfo.username,
    email: accountInfo.email,
    password: accountInfo.password,
    org: {
      id: unitAddress.operationCode,
      type: orgType,
    },
    userData: {
      title: userInfo.jobTitleId,
      name: userInfo.name,
      miscData: {
        ...removeEmptyKeys({
          JobPosition: userInfo.jobPosition,
          Address: userInfo.address,
          IdDoc: userInfo.cccd,
          IdDocIssuedAt: userInfo.issueDate,
          IdDocIssuedBy: userInfo.issuePlace,
          Dob: userInfo.dob,
          Gender: userInfo.genderId,
        }),
        Phone: userInfo.phone,
      },
    },
    expiredAt: accountInfo.expiredAt || undefined,
    roleAssignment: formatRolesAssignments(rolesToAssign, isCQQL),
  };
}
